import React, { useState, useEffect, useMemo, useRef } from "react";
import API from "../../api/axiosInstance";
import {
  utcToLADateKey,
  laDateKeyToUTC,
  travelDateTimeToLADateKey,
  formatLADateKeyOrRangeForDisplay,
} from "../../utils/laDateUtils";
import ParticipantPickerTable from "./ParticipantPickerTable";
import { EmsDateRangeEmphasis } from "./EmsDateRangeEmphasis";
import ExtraServicesEditor from "./ExtraServicesEditor";
import "../css/AllocationModal.css";

const formatShort = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

/** Merge per-night extra_services across an edit-mode booking group into one list,
 * annotating a service with `date_keys` only when it doesn't apply to every night —
 * mirrors BookingRoutes.js's mergeExtraServicesForRun. Without this, edit mode only
 * ever showed the first night's services, silently discarding the rest. */
const mergeExtraServicesAcrossBookings = (bookings) => {
  const bySignature = new Map();
  for (const b of bookings) {
    for (const svc of b.extra_services || []) {
      const sig = `${svc.service_name}|${svc.price}|${svc.allocation_type || "required"}`;
      if (!bySignature.has(sig)) {
        bySignature.set(sig, {
          service_name: svc.service_name,
          price: svc.price,
          allocation_type: svc.allocation_type,
          date_keys: [],
        });
      }
      bySignature.get(sig).date_keys.push(b.la_date_key);
    }
  }
  const total = bookings.length;
  return Array.from(bySignature.values()).map((svc) => {
    if (svc.date_keys.length === total) {
      const { date_keys, ...rest } = svc;
      return rest;
    }
    return svc;
  });
};

const AllocationModal = ({ unit, dates, bedCount, defaultPrice, onClose, onCreated, editEntry, onUpdated, hotelName, roomType, competitionId }) => {
  const isEditMode = !!editEntry;
  const firstBooking = isEditMode ? editEntry.bookings[0] : null;

  const [people, setPeople] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(
    isEditMode ? (firstBooking?.participant_id?._id || firstBooking?.participant_id || "") : ""
  );
  const [allocationType, setAllocationType] = useState(
    isEditMode
      ? (firstBooking?.allocation_type === "standard" ? "required" : firstBooking?.allocation_type || "required")
      : "required"
  );
  const [selectedGuestIds, setSelectedGuestIds] = useState(
    isEditMode ? (firstBooking?.selected_guest_ids?.map((id) => (typeof id === "object" && id?._id ? id._id : String(id))) || []) : []
  );
  const [isGuestOnly, setIsGuestOnly] = useState(
    isEditMode ? (firstBooking?.is_guest_only || false) : false
  );
  const [price, setPrice] = useState(
    isEditMode ? (firstBooking?.price ?? defaultPrice) : defaultPrice
  );
  const [services, setServices] = useState(
    isEditMode ? mergeExtraServicesAcrossBookings(editEntry.bookings) : []
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loadingParticipants, setLoadingParticipants] = useState(true);

  const dateLabel = isEditMode
    ? formatLADateKeyOrRangeForDisplay(editEntry.dateLabel)
    : dates.length > 1
      ? `${formatShort(dates[0])} to ${formatShort(dates[dates.length - 1])}`
      : dates.length === 1
        ? formatShort(dates[0])
        : "";

  useEffect(() => {
    const fetchParticipants = async () => {
      setLoadingParticipants(true);
      try {
        const res = await API.get("/ems/participants?include_org=true&slim=1");
        const data = Array.isArray(res.data) ? res.data : [];

        const built = data
          .filter((p) => {
            if (p.is_deleted) return false;
            const groups = Array.isArray(p.group_id) ? p.group_id : p.group_id ? [p.group_id] : [];
            return !groups.some((g) => g?.group_name?.toLowerCase().includes("athlete")) && p.person_id?._id;
          })
          .map((p) => {
            let meta = {};
            try { meta = typeof p.person_data === "string" ? JSON.parse(p.person_data) : (p.person_data || {}); } catch {}

            const fullName = `${meta.givenFirstName || p.person_id?.name || ""} ${meta.familyLastName || p.person_id?.lname || ""}`.trim() || "(No name)";
            const arrivalKey = travelDateTimeToLADateKey(meta.dateTime) || meta.arrival_la_date_key || null;
            const departureKey = travelDateTimeToLADateKey(meta.departureDateTime1) || meta.departure_la_date_key || null;

            const rawRows = meta?.dataGrid || meta?.guestDetails?.dataGrid || meta?.guest?.dataGrid || [];
            const guests = Array.isArray(rawRows)
              ? rawRows
                  .map((row, index) => {
                    const name = (row && (row.fullName1 || row.fullName || row.email))?.trim();
                    const id = row?.participant_guest_id || row?._id || `guest-${index}`;
                    return name ? { id, index, name } : null;
                  })
                  .filter(Boolean)
              : [];

            return {
              _id: p.person_id._id,
              participant_id: p._id,
              fullName,
              organisation: p.organisation_name || "N/A",
              arrivalDisplay: meta.dateTime ? new Date(meta.dateTime) : null,
              departureDisplay: meta.departureDateTime1 ? new Date(meta.departureDateTime1) : null,
              arrival_la_date_key: arrivalKey,
              departure_la_date_key: departureKey,
              guests,
            };
          });

        setPeople(built);
      } catch (err) {
        console.error("Failed to fetch participants:", err);
      } finally {
        setLoadingParticipants(false);
      }
    };
    fetchParticipants();
  }, []);

  const prevParticipantRef = useRef(selectedParticipant);
  useEffect(() => {
    if (prevParticipantRef.current !== selectedParticipant) {
      setSelectedGuestIds([]);
      prevParticipantRef.current = selectedParticipant;
    }
  }, [selectedParticipant]);

  const selectedDateKeys = useMemo(() => (dates || []).map((d) => utcToLADateKey(d)), [dates]);

  // Night picker: lets a service apply to any subset of the stay's nights. In
  // create mode, `dates` is the real set of nights about to be booked. In edit
  // mode, `editEntry.bookings` are the group's real per-night booking documents —
  // toggling a night here excludes it from that service on Save (per-night PATCH,
  // see handleSubmit) rather than the old uniform-apply-to-every-night behavior.
  const nightOptions = useMemo(() => {
    if (isEditMode) {
      if (!editEntry?.bookings || editEntry.bookings.length <= 1) return null;
      return [...editEntry.bookings]
        .sort((a, b) => (a.la_date_key || "").localeCompare(b.la_date_key || ""))
        .map((b) => ({ key: b.la_date_key, label: formatShort(laDateKeyToUTC(b.la_date_key)) }));
    }
    if (!dates || dates.length <= 1) return null;
    return dates.map((d, i) => ({ key: selectedDateKeys[i], label: formatShort(d) }));
  }, [isEditMode, editEntry, dates, selectedDateKeys]);

  const isParticipantAvailable = (p) => {
    if (!p.arrival_la_date_key || !p.departure_la_date_key) return true;
    if (selectedDateKeys.length === 0) return true;
    const arrivalDate = laDateKeyToUTC(p.arrival_la_date_key);
    const departureDate = laDateKeyToUTC(p.departure_la_date_key);
    return selectedDateKeys.every((key) => {
      const bookingDate = laDateKeyToUTC(key);
      return arrivalDate <= bookingDate && bookingDate <= departureDate;
    });
  };

  const handleSubmit = async () => {
    if (!selectedParticipant) {
      setError("Please select a participant");
      return;
    }
    const guestCount = selectedGuestIds.length;
    if (isGuestOnly && guestCount === 0) {
      setError("At least 1 guest required for guest-only booking");
      return;
    }
    setSubmitting(true);
    setError("");

    if (isEditMode) {
      const before = {
        guest_count: Number(firstBooking?.guest_count || 0),
        allocation_type: (firstBooking?.allocation_type === "standard" ? "required" : (firstBooking?.allocation_type || "required")),
        allocation_type_label:
          (firstBooking?.allocation_type === "private")
            ? "Private"
            : (firstBooking?.allocation_type ? "Required" : "-"),
        is_guest_only: !!firstBooking?.is_guest_only,
        is_guest_only_label: firstBooking?.is_guest_only ? "Yes" : "No",
        price: firstBooking?.price ?? defaultPrice,
      };

      const after = {
        guest_count: Number(selectedGuestIds.length),
        allocation_type: allocationType,
        allocation_type_label: allocationType === "private" ? "Private" : "Required",
        is_guest_only: !!isGuestOnly,
        is_guest_only_label: isGuestOnly ? "Yes" : "No",
        price: price != null ? Number(price) : defaultPrice,
      };

      const didChange =
        before.guest_count !== after.guest_count ||
        before.allocation_type !== after.allocation_type ||
        before.is_guest_only !== after.is_guest_only ||
        Number(before.price) !== Number(after.price);

      const failures = [];
      const successes = [];
      for (const b of editEntry.bookings) {
        // Per-night scoping: a service with no date_keys applies to every night;
        // one with date_keys only applies where its night is listed. Stripped of
        // the date_keys field itself before saving — that's display-only, mirrors
        // the same stripping the create-mode per-night POST loop already does.
        const servicesForNight = services
          .filter((s) => !s.date_keys || s.date_keys.includes(b.la_date_key))
          .map(({ service_name, price: svcPrice, allocation_type: svcAllocationType }) => ({
            service_name,
            price: svcPrice,
            allocation_type: svcAllocationType,
          }));
        try {
          await API.patch(`/ems/room-bookings/${b._id}`, {
            guest_count: selectedGuestIds.length,
            selected_guest_ids: selectedGuestIds.length > 0 ? selectedGuestIds : [],
            is_guest_only: isGuestOnly,
            allocation_type: allocationType,
            price: price != null ? Number(price) : undefined,
            extra_services: servicesForNight,
          });
          successes.push(b);
        } catch (err) {
          failures.push({ id: b._id, message: err.response?.data?.message || "Failed" });
        }
      }

      if (didChange && successes.length > 0) {
        const keys = [...new Set((editEntry.bookings || []).map((b) => b?.la_date_key).filter(Boolean))].sort();
        const checkin = keys[0] || "";
        const checkout = keys[keys.length - 1] || "";
        const total = keys.length || editEntry.bookings.length || 0;

        API.post("/ems/room-bookings/send-update", {
          participant_id: selectedParticipant,
          checkin_date: checkin,
          checkout_date: checkout,
          total_nights: total,
          competition_id: competitionId,

          hotel_name: hotelName,
          room_type: roomType,
          room_label: unit?.room_label || "",
          guest_count: after.guest_count,
          allocation_type: after.allocation_type,
          allocation_type_label: after.allocation_type_label,
          price: after.price,
          is_guest_only_label: after.is_guest_only_label,

          before_hotel_name: hotelName,
          before_room_type: roomType,
          before_room_label: unit?.room_label || "",
          before_guest_count: before.guest_count,
          before_allocation_type_label: before.allocation_type_label,
          before_price: before.price,
          before_is_guest_only_label: before.is_guest_only_label,
          before_checkin_date: checkin,
          before_checkout_date: checkout,
          before_total_nights: total,
        }).catch((err) => console.error("Update email failed:", err));
      }

      setSubmitting(false);
      if (failures.length === editEntry.bookings.length) {
        setError(failures.map((f) => f.message).join("; "));
      } else if (failures.length > 0) {
        setError(`Updated ${editEntry.bookings.length - failures.length} of ${editEntry.bookings.length}. Some failed.`);
        onUpdated();
      } else {
        onUpdated();
      }
    } else {
      const failures = [];
      const successfulDateKeys = [];
      for (const d of dates) {
        const dateKey = utcToLADateKey(d);
        const servicesForNight = services
          .filter((s) => !s.date_keys || s.date_keys.includes(dateKey))
          .map(({ service_name, price: svcPrice, allocation_type: svcAllocationType }) => ({
            service_name,
            price: svcPrice,
            allocation_type: svcAllocationType,
          }));
        try {
          await API.post("/ems/room-bookings", {
            room_unit_id: unit._id,
            date: dateKey,
            participant_id: selectedParticipant,
            guest_count: selectedGuestIds.length,
            selected_guest_ids: selectedGuestIds.length > 0 ? selectedGuestIds : [],
            is_guest_only: isGuestOnly,
            allocation_type: allocationType,
            price: price != null ? Number(price) : undefined,
            hotel_id: unit.hotel_id?._id || unit.hotel_id,
            extra_services: servicesForNight,
          });
          successfulDateKeys.push(dateKey);
        } catch (err) {
          failures.push({ date: formatShort(d), message: err.response?.data?.message || "Failed" });
        }
      }

      if (successfulDateKeys.length > 0) {
        const sortedKeys = [...successfulDateKeys].sort();
        API.post("/ems/room-bookings/send-confirmation", {
          participant_id: selectedParticipant,
          checkin_date: sortedKeys[0],
          checkout_date: sortedKeys[sortedKeys.length - 1],
          total_nights: sortedKeys.length,
          hotel_name: hotelName,
          room_type: roomType,
          room_label: unit?.room_label || "",
          guest_count: selectedGuestIds.length,
          allocation_type: allocationType,
          allocation_type_label: allocationType === "private" ? "Private" : "Required",
          competition_id: competitionId,
        }).catch((err) => console.error("Confirmation email failed:", err));
      }

      setSubmitting(false);
      if (failures.length === dates.length) {
        setError(failures.map((f) => `${f.date}: ${f.message}`).join("; "));
      } else if (failures.length > 0) {
        setError(`Booked ${dates.length - failures.length} of ${dates.length} dates. Failed: ${failures.map((f) => `${f.date} (${f.message})`).join("; ")}`);
        onCreated();
      } else {
        onCreated();
      }
    }
  };

  return (
    <div className="allocation-modal-overlay">
      <div className="allocation-modal-panel">
        <div className="allocation-modal-header">
          <h5 className="allocation-modal-title">
            {isEditMode ? "Edit Booking" : "Allocate Room"}
          </h5>
          <button type="button" className="allocation-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="allocation-modal-stay-orbit" aria-label="Room and date summary">
          <div className="allocation-modal-stay-orbit__head">
            <span className="allocation-modal-stay-orbit__title">Stay Summary</span>
          </div>
          <div className="allocation-modal-stay-orbit__body">
            <div className="allocation-modal-stay-orbit__summary-row">
              <div className="allocation-modal-stay-orbit__segment allocation-modal-stay-orbit__segment--room">
                <span className="allocation-modal-stay-orbit__glyph" aria-hidden="true">⌂</span>
                <div className="allocation-modal-stay-orbit__content">
                  <span className="allocation-modal-stay-orbit__key">Room</span>
                  <span className="allocation-modal-stay-orbit__value">{unit.room_label || "—"}</span>
                </div>
              </div>
              <div className="allocation-modal-stay-orbit__segment allocation-modal-stay-orbit__segment--dates">
                <div className="allocation-modal-stay-orbit__content allocation-modal-stay-orbit__content--dates">
                  <span className="allocation-modal-stay-orbit__key">Dates</span>
                  <span className="allocation-modal-stay-orbit__value allocation-modal-stay-orbit__value--date">
                    <EmsDateRangeEmphasis variant="inline">{dateLabel}</EmsDateRangeEmphasis>
                  </span>
                </div>
                <span className="allocation-modal-stay-orbit__glyph allocation-modal-stay-orbit__glyph--date" aria-hidden="true">
                  ◷
                </span>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="allocation-modal-error" role="alert">{error}</div>}

        <div className="allocation-modal-picker">
        <ParticipantPickerTable
          people={people}
          setPeople={setPeople}
          selectedParticipant={selectedParticipant}
          onSelectParticipant={setSelectedParticipant}
          isParticipantAvailable={isParticipantAvailable}
          loadingParticipants={loadingParticipants}
          selectionLocked={isEditMode}
        />
        </div>

        {selectedParticipant && (() => {
          const selected = people.find((p) => p._id === selectedParticipant);
          if (!selected) return null;
          const toggleGuest = (id) => {
            setSelectedGuestIds((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
            );
          };
          return (
            <div className="allocation-modal-guest-block">
              <div className="allocation-modal-guest-block__heading">
                Guests registered for {selected.fullName}
              </div>
              {selected.guests?.length > 0 ? (
                <ul className="allocation-modal-guest-block__list">
                  {selected.guests.map((g) => (
                    <li key={g.id || g.index} className="allocation-modal-guest-block__item">
                      <label className="allocation-modal-guest-block__check">
                        <input
                          type="checkbox"
                          checked={selectedGuestIds.includes(g.id)}
                          onChange={() => toggleGuest(g.id)}
                        />
                        <span>{g.name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="allocation-modal-guest-block__empty">No guests registered.</div>
              )}
            </div>
          );
        })()}

        <div className="allocation-modal-inline-check">
          <label>
            <input type="checkbox" checked={isGuestOnly} onChange={(e) => setIsGuestOnly(e.target.checked)} />
            <span>Guest only – participant not in this room</span>
          </label>
        </div>

        <div className="allocation-modal-field">
          <label className="allocation-modal-label" htmlFor="allocation-modal-allocation-type">
            Allocation Type
          </label>
          <select
            id="allocation-modal-allocation-type"
            className="form-control"
            value={allocationType}
            onChange={(e) => setAllocationType(e.target.value)}
          >
            <option value="required">Required</option>
            <option value="private">Private</option>
          </select>
        </div>

        <div className="allocation-modal-row2">
          <div className="allocation-modal-field">
            <label className="allocation-modal-label" htmlFor="allocation-modal-guest-count">
              No. of Guests
            </label>
            <input
              id="allocation-modal-guest-count"
              type="number"
              className="form-control allocation-guest-count-input"
              min={0}
              max={bedCount}
              value={selectedGuestIds.length}
              readOnly
              title="Determined by selected guests above"
            />
          </div>
          <div className="allocation-modal-field">
            <label className="allocation-modal-label" htmlFor="allocation-modal-price">
              Price
            </label>
            <input
              id="allocation-modal-price"
              type="number"
              className="form-control"
              min={0}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
        </div>

        <div className="allocation-modal-field">
          <label className="allocation-modal-label">Additional Services</label>
          <ExtraServicesEditor
            services={services}
            onChange={setServices}
            nights={nightOptions}
            totalNights={isEditMode ? editEntry.bookings.length : undefined}
          />
        </div>

        <div className="allocation-modal-footer">
          <button
            type="button"
            className="btn btn-secondary allocation-modal-action-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-warning allocation-modal-action-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : isEditMode ? "Update" : "Allocate"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllocationModal;
